import { FC, HTMLAttributes } from "react";
import * as _ from "lodash";

import { classname } from "@ctra/utils";
import NumericValue from "../NumericValue";
import styles from "./ProgressDial.module.less";

enum Behavior {
  asc = "Clockwise",
  dsc = "AntiClockwise"
}

export interface ProgressDialProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * starting value to compare with
   */
  startValue: number;
  /**
   * current value to show in the middle
   */
  currentValue: number;
  /**
   * target value to adjust the dial to (always represents 100%)
   */
  target: number;
  /**
   * unit (ideally in a final, translated form)
   */
  unit: string;
  /**
   * loading indicator
   */
  isLoading?: boolean;
  /**
   * allow the widget to display exceeding expectations when the current value is better than the target
   */
  maySpillover?: boolean;
}

/**
 * Game Plan progress dial component
 * @param {boolean | undefined} isLoading
 * @param {boolean | undefined} maySpillover
 * @param {number} startValue
 * @param {number} currentValue
 * @param {number} target
 * @param {string} unit
 * @param {string | undefined} className
 * @param {Pick<ProgressDialProps & {children?: React.ReactNode | undefined}, "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "children" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const ProgressDial: FC<ProgressDialProps> = ({
  isLoading,
  maySpillover = true,
  startValue,
  currentValue,
  target,
  unit,
  className,
  ...rest
}) => {
  /**
   * Keep these in sync with the CSS file!
   */
  const wh = 120,
    stroke = 6,
    radius = (wh - stroke) / 2;

  if (isLoading) {
    return (
      <div className={classname("ctra-gamePlan-progressDial", styles.ProgressDial, className)} {...rest}>
        <svg viewBox={`0 0 ${wh} ${wh}`}>
          <circle cx={wh / 2} cy={wh / 2} className={classname(styles.Circle)} />
        </svg>
      </div>
    );
  }

  /**
   * Get a CSS value for dash offset to represent an arc as a percentage of the full circle
   * @param {number} percentage
   * @return {number}
   */
  const getDialCSSValue = (percentage: number) => {
    const circumference = radius * Math.PI * 2;

    return (
      circumference +
      _.clamp(
        circumference * (behavior === Behavior.dsc ? 1 - percentage : percentage),
        -circumference,
        circumference
      )
    );
  };

  /**
   * We take a note on the expected behavior of the dial. Depending on the start and target values
   * it may move clockwise or anti-clockwise.
   * @type {Behavior.asc | Behavior.dsc}
   */
  const behavior = startValue <= target ? Behavior.asc : Behavior.dsc;

  /**
   * Depending on what we expect, we consider either the start or the target value the comparison baseline
   * @type {number}
   */
  const floor = startValue <= target ? startValue : target;

  /**
   * Depending on what we expect, we consider either the start or the target value a full circle
   * @type {number}
   */
  const ceiling = startValue > target ? startValue : target;

  /**
   * Current value as a percentage between the floor and the ceiling
   * @type {number}
   */
  const currentPercentage = (currentValue - floor) / (ceiling - floor);

  /**
   * Position of the current value on the dial (this may be invisible if we underperform)
   * @type {number}
   */
  const currentDialValue = _.clamp(currentPercentage, -1, 2);

  /**
   * Tell whether the change is in the wrong direction (aka. performing below expectation)
   * @type {boolean}
   */
  const isWrongDirection =
    (behavior === Behavior.dsc && currentDialValue > 1) ||
    (behavior === Behavior.asc && currentDialValue < 0);

  /**
   * Tell how much we outperform the target
   * @type {number}
   */
  const overTheCeilingDialValue =
    maySpillover && behavior === Behavior.asc && currentValue > ceiling ? currentPercentage - 1 : 0;

  /**
   * Tell how much we perform under the floor
   * @type {number}
   */
  const underTheFloorDialValue =
    maySpillover && behavior === Behavior.dsc && currentValue < floor ? -currentDialValue : 0;

  return (
    <div className={classname("ctra-gamePlan-progressDial", styles.ProgressDial, className)} {...rest}>
      <svg viewBox={`0 0 ${wh} ${wh}`}>
        {/* target as 100% unless the start value is higher */}
        <circle cx={wh / 2} cy={wh / 2} className={classname(styles.Circle)} />

        {/* current dial, positioned under the current one to see growth */}
        <circle
          cx={wh / 2}
          cy={wh / 2}
          className={classname(
            styles.CurrentValueArc,
            styles[behavior],
            isWrongDirection ? styles.WrongDirectionArc : null
          )}
          style={{ strokeDashoffset: getDialCSSValue(currentDialValue) }}
        />

        {/* over the ceiling as a sign of outperforming */}
        {overTheCeilingDialValue && (
          <circle
            cx={wh / 2}
            cy={wh / 2}
            className={classname(styles.OverTheCeilingArc, styles[behavior])}
            style={{ strokeDashoffset: getDialCSSValue(overTheCeilingDialValue) }}
          />
        )}

        {/* under the floor as a sign of outperforming */}
        {underTheFloorDialValue && (
          <circle
            cx={wh / 2}
            cy={wh / 2}
            className={classname(styles.UnderTheFloorArc, styles[behavior])}
            style={{
              strokeDashoffset: getDialCSSValue(1 - underTheFloorDialValue)
            }}
          />
        )}
      </svg>
      <NumericValue
        primary
        value={currentValue}
        unit={unit}
        className={classname(styles.NumericValue, isWrongDirection ? styles.WrongDirectionNumber : null)}
      />
    </div>
  );
};

export default ProgressDial;
